import { podProblemEnum } from "./app-pods.js";
import { computeResolutionSchema } from "./compute-resolution.js";
import { z } from "zod";
//#region src/app-diagnostics.ts
/**
* E6 app diagnostics wire types —
* `GET /v1/admin/apps/:appId/dps/:dpId/diagnostics` (site-admin-api; #542).
*
* *"Why isn't my app rolling on this data plane?"* — the per-DP answer the
* existing `GET /v1/apps/:appId/rollout-diagnostics` cannot give. That endpoint
* evaluates the app's single newest composition **globally** and emits two
* reason codes; this one reports every reason the **per-DP desired-state
* builder** withheld the app, for the DP the caller asked about.
*
* ## Where these values come from — and why they cannot drift
*
* `@zion/sdk-contracts`'s sibling `app-rollout-diagnostics.ts` states the
* no-drift principle for its two codes: the read is *"a PURE RECOMPUTE …
* computed via the SAME extracted predicates the builder uses, so the read can
* never drift from what the operator actually deploys."*
*
* E6 takes that one step further. Rather than sharing its *predicates* with
* `computeDesiredState`, it shares the *execution*: release-engine threads a
* `DiagnosticsSink` through the builder itself and re-runs
* `computeDesiredState(dpId, platformChannelId)` in dry-run mode with a
* collecting sink attached. Every {@link WithholdCode} below is emitted from
* the exact `continue` / `return` / `wip.delete` that withholds the app on the
* operator's real 30-second poll. Same code path, not merely the same rule —
* so "the read can never drift" is a structural property here, not a
* convention someone has to maintain.
*
* The sink is a no-op object on the operator's path and allocates nothing
* there; the desired-state document and its ETag are byte-identical with the
* sink attached and detached (regression-tested in release-engine).
*
* ## Fourteen codes, and not the fourteen the design prose meant.
*
* ⛔ The design prose, issue #542 and several plan documents all say **14**.
* They counted a table numbered 1–14 with a `13b` row inserted, which made
* fifteen; `FIXTURE_ROW_IDENTITY_MISMATCH` was the sixteenth and carries a
* `3b` for the same reason. The 40.0.0 major then removed two codes
* (`MISSING_VERSION_ID` and `LEGACY_MISSING_IMAGE_REF`, R-077 item 2, R-081).
* So {@link withholdCodeSchema} has fourteen members today, and they are not
* the old 1–14: `3b` and `13b` are members, and 2 and 7 are not. A reader who
* trusts the old numbering drops {@link WITHHOLD_REMEDY.NO_BASE_CHANNEL} and
* the omission looks like a complete implementation.
*/
/**
* Every reason the per-DP desired-state builder withholds an app, in the order
* the builder can reach them.
*
* Each member except `INVALID_LAUNCH_TARGET` is emitted from exactly one exit in
* `release-engine/src/modules/operator-api/operator-api.service.ts`. The exits
* are cited by **anchor symbol, never by line number** — every line reference
* in E6's source specifications had drifted 5–20 lines by the time it was read,
* in a 2,899-line file.
*
*  1. `MISSING_COMPOSITION`        — `if (!sv.compositionId || !sv.composition)`
*  3. `EMPTY_RESOLVED_FIXTURES`    — `if (!comp.resolvedFixtures) return;`
*  3b.`FIXTURE_ROW_IDENTITY_MISMATCH` — the `loadNewestFixtureVersionRows` identity check
*  4. `PENDING_REQUIRED_CONFIG`    — `built1.kind === 'pending'` → `wip.delete`
*  5. `MISSING_IMAGE_REF`          — `imageRefRolloutBlock(...) !== null`
*  6. `BELOW_MIN_PLATFORM_VERSION` — `if (av.minDpVersion && av.minDpVersion > platformVersion)`
*  8. `NO_ACTIVE_ORGS_ON_DP`       — `if (orgs.length === 0) return;`
*  9. `NO_APPS`                    — `if (allApps.length === 0) return;`
* 10. `NO_CHANNEL_PINS`            — `if (pins.length === 0) return;`
* 11. `PIN_HAS_NO_VERSION`         — `if (pinnedVersionIds.length === 0) return;`
* 12. `PIN_DANGLES`                — the `validPins` filter
* 13. `NO_PIN_ON_ORG_RING`         — `if (!pin) continue;`
* 13b.`NO_BASE_CHANNEL`            — `baseByOrgId.get(org.id) ?? stable?.id` is `undefined`
* 14. `INVALID_LAUNCH_TARGET`      — no exit since R2 (R-081 item 1). The
*     exit was `launchesValid = false`, and the member stays until R3
*     (R-086 item 3).
*
* The numbers keep their old positions. The 40.0.0 major removed exits 2
* (`MISSING_VERSION_ID`) and 7 (`LEGACY_MISSING_IMAGE_REF`): release-engine
* emits neither since R1 (R-077 item 2), and R2 removes both members (R-081,
* R-083).
*/
const WITHHOLD_CODES = [
	"MISSING_COMPOSITION",
	"EMPTY_RESOLVED_FIXTURES",
	"FIXTURE_ROW_IDENTITY_MISMATCH",
	"PENDING_REQUIRED_CONFIG",
	"MISSING_IMAGE_REF",
	"BELOW_MIN_PLATFORM_VERSION",
	"NO_ACTIVE_ORGS_ON_DP",
	"NO_APPS",
	"NO_CHANNEL_PINS",
	"PIN_HAS_NO_VERSION",
	"PIN_DANGLES",
	"NO_PIN_ON_ORG_RING",
	"NO_BASE_CHANNEL",
	"INVALID_LAUNCH_TARGET"
];
const withholdCodeSchema = z.enum(WITHHOLD_CODES);
/**
* Whether a reason is attributable to the app the caller asked about.
*
* - `'app'`        — this app (and possibly only this app) is withheld by it.
* - `'data-plane'` — the condition is **not attributable to any one app**. It
*   is a data-plane- or tenant-level fault that stops app resolution before
*   any app is considered, and it is therefore reported identically on
*   **every** app's diagnostics for this DP.
*
* ⛔ Without this discriminator the response cannot distinguish *"your app is
* broken"* from *"this data plane has no tenants on it."* Both would arrive as
* a `withheld[]` entry with `fixtureId: null`, and an app owner would read an
* operator-side infrastructure gap as a defect in their own app — the same
* conflation of distinct absences (`empty` ≠ `errorstate` ≠ `notcollected`)
* that this whole surface exists to eliminate. It also decides who to route
* the {@link WITHHOLD_REMEDY} to: every `'data-plane'` remedy is an action
* only a platform operator can take.
*/
const withholdScopeSchema = z.enum(["app", "data-plane"]);
/**
* `code → scope`. Five of the fourteen are not app-attributable:
* `NO_ACTIVE_ORGS_ON_DP`, `NO_APPS`, `NO_CHANNEL_PINS` and `PIN_HAS_NO_VERSION`
* are early `return`s from `mergeAppChannelPins` that abandon pin resolution
* for the whole data plane, and `NO_BASE_CHANNEL` is decided per *org*, before
* any app is in scope.
*/
const WITHHOLD_SCOPE = {
	MISSING_COMPOSITION: "app",
	EMPTY_RESOLVED_FIXTURES: "app",
	FIXTURE_ROW_IDENTITY_MISMATCH: "app",
	PENDING_REQUIRED_CONFIG: "app",
	MISSING_IMAGE_REF: "app",
	BELOW_MIN_PLATFORM_VERSION: "app",
	NO_ACTIVE_ORGS_ON_DP: "data-plane",
	NO_APPS: "data-plane",
	NO_CHANNEL_PINS: "data-plane",
	PIN_HAS_NO_VERSION: "data-plane",
	PIN_DANGLES: "app",
	NO_PIN_ON_ORG_RING: "app",
	NO_BASE_CHANNEL: "data-plane",
	INVALID_LAUNCH_TARGET: "app"
};
/**
* `code → remedy` — the human next action, one per code.
*
* Static per code, and therefore derived here at the read boundary rather than
* carried through the builder: the sink runs inside release-engine's hottest
* path and must not build strings it does not need.
*
* These are deliberately *instructions*, not restatements of the fault — the
* `detail` field already says what is wrong. Each `'data-plane'`-scoped remedy
* names a platform-operator action, because an app owner cannot perform it.
*
* ## ⛔ THE PROSE SAYS "release channel". THE CODES KEEP `RING`. BOTH ON PURPOSE.
*
* R7-DEC-6 (owner, 2026-08-05): an operator reads **"release channel"**, never
* "ring". Six of these sentences used to say "ring", which put the wire
* vocabulary in front of a human on every surface that prints a remedy — the
* Rollout screen, `zion-ops`, and the MCP tool output.
*
* ⛔ **Do not "harmonise" the identifiers to match.** `Ring`, `RINGS`,
* `RING_ORDINAL`, `NO_PIN_ON_ORG_RING` and the `?ring=` / `:ring` URL segments
* are **SENT** values: renaming one is an API break, not a copy edit. These
* strings are **READ** values — nothing in the platform compares a remedy
* sentence to anything, so they are display text and the ruling applies to them.
* Verified before this rewrite (2026-08-05): every `WITHHOLD_REMEDY` consumer
* (`site-admin-api`'s diagnostics service, `cp-admin-ui`'s `dp-model`,
* `adoption-model` and `dp-diagnostics-model`, and this module's own spec)
* references the MAP, and no golden, snapshot, doc or assertion anywhere pins a
* remedy sentence — swept for all six literal fragments across the whole
* repository, against a positive control on a seventh.
*
* ⇒ the fix belongs HERE rather than at each render site: one sentence, so the
* screen, the CLI and the log agree. A client-side rewrite would have given an
* operator two versions of one instruction with no way to tell which is current
* — the exact drift `cp-admin-ui`'s `adoption-model.ts` forbids.
*
* ⚠️ `stable` inside {@link WITHHOLD_REMEDY.NO_BASE_CHANNEL} is a **channel
* name**, i.e. a wire value, and stays. `app-diagnostics.spec.ts` asserts no
* value contains a standalone "ring", so this cannot regress silently.
*/
const WITHHOLD_REMEDY = {
	MISSING_COMPOSITION: "The channel version references an app but no composition was ever built for it. Re-publish the app so a composition is created, then re-pin the channel to it.",
	EMPTY_RESOLVED_FIXTURES: "The published composition resolved to zero fixtures, so there is nothing to deploy. Check that the app manifest declares at least one fixture, then re-publish and re-pin.",
	FIXTURE_ROW_IDENTITY_MISMATCH: "The version row resolved for this fixture carries a different fixture name or version than the one pinned, so the app is withheld rather than deployed from an unverified build. Re-publish the pinned fixture version. If it recurs, the version registry disagrees with the pin and needs investigating.",
	PENDING_REQUIRED_CONFIG: "A required configuration key has no value and no default. Set it for this fixture under Apps → Configuration (the key is in `configKey`); the app rolls out automatically once it is set.",
	MISSING_IMAGE_REF: "The pinned fixture version has no container image recorded. Re-publish that fixture version so its image reference is populated.",
	BELOW_MIN_PLATFORM_VERSION: "The fixture requires a newer data plane than this one is running. Upgrade the data plane to at least the version named in `detail`, or publish a build with a lower minimum platform version.",
	NO_ACTIVE_ORGS_ON_DP: "Platform operator: no active organization is placed on this data plane, so no app is resolved for it at all. Place a tenant on it under Platform → Data planes → Tenants. This is not a fault in your app.",
	NO_APPS: "Platform operator: the app registry is empty, so channel pins are never evaluated. Publish an app before pinning it to a release channel.",
	NO_CHANNEL_PINS: "Platform operator: no app is pinned to any release channel, so this data plane resolves no app versions. Pin at least one app to a release channel under Apps → Release chain.",
	PIN_HAS_NO_VERSION: "Platform operator: every release-channel pin is empty. Promote a version onto the channel so the pins resolve to a composition.",
	PIN_DANGLES: "The release-channel pin points at a composition that no longer exists — it was deleted or replaced without re-pinning. Re-pin the channel to a current version of the app.",
	NO_PIN_ON_ORG_RING: "This app is not pinned on the release channel that tenant follows, so that tenant resolves no version of it. Pin the app on that channel, or move the tenant to a channel where it is pinned.",
	NO_BASE_CHANNEL: "Platform operator: the tenant has no base channel set and there is no `stable` channel to fall back to. Set the tenant’s base channel, or create the `stable` channel.",
	INVALID_LAUNCH_TARGET: "A fixture declares a launch target that is not a per-instance fixture in this data plane’s desired state, which withholds the whole app. Remove the `launches` declaration, or publish the target as a per-instance fixture and pin it here."
};
/** One reason the app is withheld from this data plane's desired state. */
const withholdReasonSchema = z.object({
	/** Which builder exit produced this reason. */
	code: withholdCodeSchema,
	/**
	* Whether this reason is about the requested app or about the data plane.
	* Always `WITHHOLD_SCOPE[code]` — carried on the wire so a client does not
	* have to keep its own copy of that table in sync.
	*/
	scope: withholdScopeSchema,
	/**
	* The fixture the reason is about, or `null` when it is not
	* fixture-specific. Always `null` when `scope === 'data-plane'`.
	*/
	fixtureId: z.string().nullable(),
	/**
	* What is wrong, with the concrete identifiers substituted in.
	*
	* Provenance of the fourteen codes is 2 / 1 / 2 / 1 / 8. Before the
	* 40.0.0 major removed two verbatim codes it was 5 / 2 / 8 plus
	* `FIXTURE_ROW_IDENTITY_MISMATCH`, not the "6 logged, 9 silent" the design
	* prose claimed:
	*
	* - **2 verbatim** (`MISSING_COMPOSITION`, `BELOW_MIN_PLATFORM_VERSION`):
	*   byte-identical to the builder's existing log line, which emits from the
	*   same `const`. An operator grepping release-engine and an app owner
	*   reading this endpoint see one sentence.
	* - **1 verbatim with no exit** (`INVALID_LAUNCH_TARGET`): its text was the
	*   builder's log line, and R2 deleted that exit (R-081 item 1), so
	*   release-engine emits the code no more.
	* - **2 from the shared predicate** (`MISSING_IMAGE_REF`,
	*   `PENDING_REQUIRED_CONFIG`) — `rollout-diagnostics.util`'s own `detail`,
	*   which is what `/rollout-diagnostics` returns, so the two endpoints can
	*   never disagree about a fixture they both report. ⚠️ These are *not*
	*   verbatim the log line: the builder wraps them
	*   (`App <id> excluded from desired state: <detail>`), and its wrapper is
	*   additionally wrong for `MISSING_IMAGE_REF` — see that exit's comment.
	* - **1 authored and shared with the caller** (`FIXTURE_ROW_IDENTITY_MISMATCH`,
	*   exit 3b): the builder writes the text, records it here and returns the
	*   same `detail` to its caller as `{ kind: 'pending', detail }`, so the
	*   caller's WARN carries the same sentence.
	* - **8 authored** — the genuinely silent exits, which log nothing at all
	*   and so have no existing text to inherit.
	*/
	detail: z.string(),
	/** Present only for `PENDING_REQUIRED_CONFIG`: the unsatisfied schema key. */
	configKey: z.string().optional(),
	/** The human next action — always `WITHHOLD_REMEDY[code]`. */
	remedy: z.string()
}).strict();
/**
* Pod-level problems for this app on this data plane — the *running* half of
* the answer, where `withheld[]` is the *not-deployed* half.
*
* Sourced from E3's pod read, in-process (both endpoints live in
* site-admin-api). E6 does not fetch pods itself; a second fetch path would be
* a second thing to keep correct.
*
* ## ⛔ This reuses E3's `podProblemEnum`. It does NOT define its own.
*
* An earlier draft gave E6 a parallel SCREAMING_CASE vocabulary
* (`CRASHLOOP | IMAGE_PULL | OOMKILLED | NOT_READY`) mapped from E3's values.
* That mapping was **one row short**: `CreateContainerConfigError` — the
* "your `envFrom` names a Secret that does not exist" failure, which is a
* misconfigured app and therefore exactly this endpoint's audience — had no
* target and would have been dropped in silence. `/pods` would show the
* problem on the same screen where this endpoint reported "nothing wrong",
* and the reassuring one is the one an app owner reads first.
*
* The bug was not a wrong row. It was a **missing** row that no type checked.
* A fifth code would have closed today's gap and left the mechanism that
* produced it running, so the enum is shared instead: there is one definition,
* it cannot drift, and the next problem E3 learns to derive arrives here for
* free rather than vanishing. `app-diagnostics.spec.ts` asserts every
* `POD_PROBLEMS` value is expressible here, so an eighth breaks the build.
*
* One code the design doc lists is absent, and the difference matters:
*
* - **`MIGRATION_FAILED` — nothing anywhere produces it.** `grep -rn
*   MIGRATION_FAILED cloud tools --include='*.ts'` (excluding `dist`) returns
*   **0**, against a **23**-hit positive control on the same grep form.
* - **`Unschedulable` — NOW PRESENT (compute-classes D7).** It was struck from
*   E3 by ruling R4-03 because the fact lives on the pod condition and
*   `conditions[]` is not on `PodSummarySchema`. R4-03's condition has been met
*   the way it required — by a DP release, not a client-side derivation:
*   `PodSummarySchema.unschedulable` (a single boolean,
*   `@zion/sdk-cp-dp-contracts`) is the producer, and `site-admin-api`'s
*   `pod-problems.ts` derives the member from it. ⛔ The scaling-event path is
*   STILL not the producer — see the trap note below, which stands unchanged.
*
* ⛔ **`Unschedulable` is NOT producerless in general, and the distinction is a
* trap.** `scaling-event.ts` carries a `PodScheduled` `reason` (documented
* there as *"e.g. `Unschedulable`; null when scheduled"*) and a
* `pendingUnschedulable` flag. That is the **scaling-event** producer — a
* different path, a different contract, a different trust boundary from the
* pod read this block projects. Wiring the scaling-event path into the pod
* path to "restore" the missing code would be a cross-producer shortcut
* wearing the costume of a bug fix. If `UNSCHEDULABLE` is wanted here, it
* arrives the way R4-03 says: `conditions[]` on `PodSummarySchema`, with E5.
*
* A code declared on the wire that nothing can emit is worse than an absent
* one: it reads to an app owner as *"we checked for that and it was fine."*
*/
const runtimeProblemCodeSchema = podProblemEnum;
/** One pod-level problem observed for this app on this data plane. */
const runtimeProblemSchema = z.object({
	code: runtimeProblemCodeSchema,
	/**
	* Parsed from the pod name. **Nullable, matching `AppPodSummary`** — a
	* legitimately-scoped pod can be named from the shorter `instanceKey` form
	* that carries no fixture or version. Declaring these non-null here would
	* force the producer to invent a value for a pod that genuinely has none.
	*/
	fixtureId: z.string().nullable(),
	versionSlug: z.string().nullable(),
	podName: z.string(),
	detail: z.string()
}).strict();
/**
* Why a section of this response has no data — as a value, because `[]` cannot
* carry the difference.
*
* ⛔ This is the whole point of the endpoint. Three absences that look
* identical on the wire are three different facts:
*
* - **`collected`** — we asked, and the answer is genuinely nothing. A clean
*   bill of health.
* - **`notcollected`** — nobody has ever produced this. For `withheld[]` that
*   is a data plane with no platform channel pinned: desired state has never
*   been computed for it, so "nothing is withheld" is not true, it is unknown.
* - **`errorstate`** — we asked and the attempt failed. release-engine was
*   unreachable, or the data-plane pod read failed. Rendering this as an empty
*   list tells an app owner their app is healthy on the strength of a request
*   that never completed.
*
* Collapsing `errorstate` into `collected` is the single failure this surface
* exists to eliminate, and it is the easy mistake: a `catch` that returns `[]`
* makes the endpoint feel robust and makes it lie.
*/
const sectionStatusSchema = z.enum([
	"collected",
	"notcollected",
	"errorstate"
]);
/**
* One section of the response, carrying its own provenance.
*
* `items` is always an array so a consumer can render it without narrowing —
* but it is **only meaningful when `status === 'collected'`**, and `detail` is
* present for the other two to say what happened.
*/
const sectionOf = (item) => z.object({
	status: sectionStatusSchema,
	items: z.array(item),
	/**
	* Why, when `status !== 'collected'`. Null on a clean collection — a
	* reason attached to a successful read would be noise a UI has to filter.
	*/
	detail: z.string().nullable()
}).strict();
const withheldSectionSchema = sectionOf(withholdReasonSchema);
const runtimeSectionSchema = sectionOf(runtimeProblemSchema);
/**
* compute-classes D19/D37/D40 — per-unit compute resolution provenance for this app on this DP.
*
* ⛔ WRITTEN OUT rather than `sectionOf(...).extend({...})`, and the reason is a measurement nobody
* has made: `sectionOf` returns a `.strict()` object, and whether zod 4's `.extend()` PRESERVES
* unknown-key rejection across that boundary is the one property this schema most needs. Two tests
* (`A-197`, `A-198`) pin it instead — that an unknown key IS rejected, and that the
* `status`/`items`/`detail` triple is shape-identical to a `sectionOf` instance. The identity check
* alone would pass on a schema that had quietly stopped being strict.
*
* ⚠️ `sectionStatusSchema` is what makes the three-state (`collected` / `errorstate` /
* `notcollected`) work — an un-upgraded DP renders `notcollected`, which is the whole reason D19
* reuses this shape rather than inventing a nullable field.
*/
const computeSectionSchema = z.object({
	status: sectionStatusSchema,
	items: z.array(computeResolutionSchema),
	detail: z.string().nullable(),
	/**
	* D37 — TRUNCATION IS A WIRE FACT, STATED AT THE SOURCE. Only the operator knows it capped, so
	* this is propagated from `StatusReport.computeResolutionsTruncated`, never derived.
	* ⛔ A consumer MUST NOT infer truncation from `items.length === COMPUTE_RESOLUTION_MAX`: the
	* filtered per-app list is almost never the capped DP-wide list, so that test is wrong in BOTH
	* directions — false on a truncated report, and true on an untruncated 200-unit app.
	*/
	truncated: z.boolean(),
	/**
	* D37 — the app's total row count when it is KNOWN, else `null`.
	* ⚠️ `null` whenever `truncated` is true: the cap is DP-WIDE and the report carries no per-app
	* total, so CP cannot attribute the loss to this app. Stating `null` is the honest answer;
	* echoing `items.length` there would be a count presented as a total.
	*/
	total: z.number().int().nonnegative().nullable(),
	/**
	* D40 — WHEN this answer was observed: the `received_at` of the DP status report these rows
	* were reduced from. ISO-8601; `null` when the operator has never reported at all.
	*
	* ⛔ SECTION level, not per row. Every row in one section came from the same report, so a
	* per-row copy would be N identical timestamps that can drift apart under a partial update
	* and imply a per-row freshness the producer never expressed.
	* ⛔ It is NOT `computedAt` (which the envelope already carries and means "when CP assembled
	* this response"). The distinction is the whole point: a section can be freshly COMPUTED from
	* a report that is an hour OLD, and D12's "why is this pod on this machine" is unanswerable
	* without knowing which of the two you are looking at.
	* ⚠️ The StatusReport wire is UNTOUCHED — this is a CP-side projection of a column CP already
	* persists.
	*/
	observedAt: z.string().datetime().nullable()
}).strict();
/**
* `GET /v1/admin/apps/:appId/dps/:dpId/diagnostics` — *"why isn't my app
* rolling on this data plane?"*
*
* Two independent halves, each with its own provenance, because they have
* separate producers that fail separately: `withheld` comes from
* release-engine's desired-state builder over an intra-cluster hop, and
* `runtime` comes from E3's pod read over a data-plane hop. Either can be
* `errorstate` while the other is `collected`, and a single top-level status
* would have to lie about one of them.
*/
const appDpDiagnosticsSchema = z.object({
	appId: z.string().uuid(),
	dpId: z.string().uuid(),
	/** Is the app in this DP's desired state right now? */
	present: z.boolean(),
	/** Does `dp_fixture_status` show ready replicas for it? */
	running: z.boolean(),
	withheld: withheldSectionSchema,
	runtime: runtimeSectionSchema,
	/** compute-classes D19 — per-unit resolution provenance for this app on this DP, filtered by
	*  `appId` server-side (D32). ⛔ OPTIONAL on a `.strict()` object: a REQUIRED key would break
	*  every in-flight response and every existing snapshot fixture the moment the schema ships. */
	compute: computeSectionSchema.optional(),
	/** The operator has not reported within its expected window. */
	operatorStale: z.boolean(),
	/** The operator has stopped reporting entirely. */
	operatorUnresponsive: z.boolean(),
	/** ISO-8601, or null when the operator has never reported. */
	lastReportAt: z.string().datetime().nullable(),
	/** ISO-8601 — when this answer was computed. */
	computedAt: z.string().datetime()
}).strict();
//#endregion
export { WITHHOLD_CODES, WITHHOLD_REMEDY, WITHHOLD_SCOPE, appDpDiagnosticsSchema, computeSectionSchema, runtimeProblemCodeSchema, runtimeProblemSchema, runtimeSectionSchema, sectionStatusSchema, withheldSectionSchema, withholdCodeSchema, withholdReasonSchema, withholdScopeSchema };

//# sourceMappingURL=app-diagnostics.js.map